"use client";
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';

/* ── Keyframe animations injected once ── */
const AnimStyles = () => (
    <style>{`
        /* Glow pulse for highlighted boxes */
        @keyframes ux-glow-pulse {
            0%, 100% { filter: drop-shadow(0 0 4px rgba(206,255,26,0.4)); opacity: 1; }
            50%       { filter: drop-shadow(0 0 14px rgba(206,255,26,0.9)); opacity: 0.85; }
        }
        /* Checkmark draw-in */
        @keyframes ux-draw {
            from { stroke-dashoffset: 40; }
            to   { stroke-dashoffset: 0; }
        }
        /* Scan line sweep */
        @keyframes ux-scan {
            0%   { transform: translateY(0px); opacity: 0.8; }
            80%  { transform: translateY(130px); opacity: 0.6; }
            100% { transform: translateY(130px); opacity: 0; }
        }
        /* Slow float */
        @keyframes ux-float {
            0%, 100% { transform: translateY(0px); }
            50%       { transform: translateY(-8px); }
        }
        /* Node pulse for site map root */
        @keyframes ux-node-pulse {
            0%, 100% { filter: drop-shadow(0 0 6px rgba(206,255,26,0.5)); }
            50%       { filter: drop-shadow(0 0 18px rgba(206,255,26,1)); }
        }
        /* Line draw for tree branches */
        @keyframes ux-line-draw {
            from { stroke-dashoffset: 200; }
            to   { stroke-dashoffset: 0; }
        }
        /* Arrow slide */
        @keyframes ux-arrow-slide {
            0%, 100% { transform: translateX(0px); opacity: 1; }
            50%       { transform: translateX(6px); opacity: 0.5; }
        }
        /* Cursor drift */
        @keyframes ux-cursor-move {
            0%        { transform: translate(0px, 0px); }
            25%       { transform: translate(10px, -8px); }
            50%       { transform: translate(20px, 5px); }
            75%       { transform: translate(8px, 15px); }
            100%      { transform: translate(0px, 0px); }
        }
        /* Screen active cycle */
        @keyframes ux-screen-active {
            0%, 30%  { opacity: 0.3; }
            40%, 70% { opacity: 1; }
            80%,100% { opacity: 0.3; }
        }
        /* Palette orbit */
        @keyframes ux-palette-pop {
            0%, 100% { transform: scale(1); }
            50%       { transform: scale(1.18); }
        }
        /* Shimmer sweep */
        @keyframes ux-shimmer {
            0%   { transform: translateX(-100%); }
            100% { transform: translateX(400%); }
        }
        /* Annotation dash draw */
        @keyframes ux-dash-draw {
            from { stroke-dashoffset: 60; opacity: 0; }
            to   { stroke-dashoffset: 0; opacity: 1; }
        }
        /* Blink cursor */
        @keyframes ux-blink {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.2; }
        }
        /* Wiggle for cursor glyph */
        @keyframes ux-wiggle {
            0%, 100% { transform: rotate(0deg); }
            20%       { transform: rotate(-8deg); }
            60%       { transform: rotate(6deg); }
        }
    `}</style>
);

/* ─── Illustration 01 — Competitor Analysis ─── */
const IllustrationCompetitor = () => (
    <svg viewBox="0 0 260 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        {[0,1,2,3,4].map(col => [0,1].map(row => {
            const x = 10 + col * 50;
            const y = 20 + row * 70;
            const isHighlighted = (col === 2 && row === 0) || (col === 4 && row === 0) || (col === 0 && row === 1);
            const delays = [`0s`,`0.4s`,`0.8s`];
            const delayIdx = [(col===2&&row===0)?0:(col===4&&row===0)?1:2];
            return (
                <g key={`${col}-${row}`}>
                    <rect x={x} y={y} width="40" height="40" rx="6"
                        fill={isHighlighted ? 'rgba(206,255,26,0.08)' : 'rgba(255,255,255,0.04)'}
                        stroke={isHighlighted ? '#ceff1a' : 'rgba(255,255,255,0.12)'}
                        strokeWidth={isHighlighted ? '1.5' : '1'}
                        style={isHighlighted ? { animation: `ux-glow-pulse 2s ease-in-out ${delays[delayIdx[0]]} infinite` } : {}}
                    />
                    {isHighlighted ? (
                        <path
                            d={`M${x+10} ${y+20} l8 8 12-14`}
                            stroke="#ceff1a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                            strokeDasharray="40" strokeDashoffset="40"
                            style={{ animation: `ux-draw 0.7s ease-out ${delays[delayIdx[0]]} forwards, ux-glow-pulse 2s ease-in-out ${delays[delayIdx[0]]} infinite` }}
                        />
                    ) : (
                        <path d={`M${x+13} ${y+13} l14 14M${x+27} ${y+13} l-14 14`}
                            stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeLinecap="round"/>
                    )}
                </g>
            );
        }))}
        {/* Scanning highlight row */}
        <rect x="8" y="0" width="244" height="6" rx="3"
            fill="rgba(206,255,26,0.08)"
            style={{ animation: 'ux-scan 3s ease-in-out 0.5s infinite' }}
        />
    </svg>
);

/* ─── Illustration 02 — Site Map ─── */
const IllustrationSiteMap = () => (
    <svg viewBox="0 0 260 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        {/* Root */}
        <rect x="100" y="10" width="60" height="28" rx="5"
            fill="rgba(206,255,26,0.1)" stroke="#ceff1a" strokeWidth="1.5"
            style={{ animation: 'ux-node-pulse 2.2s ease-in-out infinite' }}/>
        {/* Trunk */}
        <line x1="130" y1="38" x2="130" y2="58" stroke="rgba(206,255,26,0.4)" strokeWidth="1.2"
            strokeDasharray="20" strokeDashoffset="20"
            style={{ animation: 'ux-line-draw 1s ease-out 0.3s forwards' }}/>
        {/* L2 branches */}
        {[40,100,160].map((x, i) => (
            <g key={i}>
                <line x1="130" y1="58" x2={x+25} y2="58" stroke="rgba(206,255,26,0.25)" strokeWidth="1"
                    strokeDasharray="100" strokeDashoffset="100"
                    style={{ animation: `ux-line-draw 0.8s ease-out ${0.5 + i*0.15}s forwards` }}/>
                <line x1={x+25} y1="58" x2={x+25} y2="68" stroke="rgba(206,255,26,0.25)" strokeWidth="1"
                    strokeDasharray="20" strokeDashoffset="20"
                    style={{ animation: `ux-line-draw 0.5s ease-out ${0.7 + i*0.15}s forwards` }}/>
                <rect x={x} y="68" width="50" height="24" rx="4"
                    fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.18)" strokeWidth="1"
                    style={{ animation: `ux-float ${2.5 + i*0.3}s ease-in-out ${i*0.4}s infinite` }}/>
                {/* L3 */}
                {[0,1].map(j => (
                    <g key={j}>
                        <line x1={x+25} y1="92" x2={x+8+j*34} y2="92" stroke="rgba(255,255,255,0.1)" strokeWidth="1"
                            strokeDasharray="40" strokeDashoffset="40"
                            style={{ animation: `ux-line-draw 0.5s ease-out ${0.9+i*0.15+j*0.1}s forwards` }}/>
                        <line x1={x+8+j*34} y1="92" x2={x+8+j*34} y2="100" stroke="rgba(255,255,255,0.1)" strokeWidth="1"
                            strokeDasharray="20" strokeDashoffset="20"
                            style={{ animation: `ux-line-draw 0.4s ease-out ${1.0+i*0.15+j*0.1}s forwards` }}/>
                        <rect x={x-2+j*28} y="100" width="24" height="16" rx="3"
                            fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                        <line x1={x+3+j*28} y1="105" x2={x+19+j*28} y2="105" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeLinecap="round"/>
                        <line x1={x+3+j*28} y1="109" x2={x+15+j*28} y2="109" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeLinecap="round"/>
                    </g>
                ))}
            </g>
        ))}
    </svg>
);

/* ─── Illustration 03 — Wireframing ─── */
const IllustrationWireframe = () => (
    <svg viewBox="0 0 260 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" style={{ overflow: 'hidden' }}>
        {/* Phone outline */}
        <rect x="80" y="5" width="100" height="150" rx="12"
            fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"/>
        {/* Nav bar */}
        <rect x="88" y="18" width="84" height="10" rx="2"
            fill="rgba(206,255,26,0.12)" stroke="rgba(206,255,26,0.4)" strokeWidth="1"
            style={{ animation: 'ux-glow-pulse 3s ease-in-out infinite' }}/>
        {/* Hero block */}
        <rect x="88" y="34" width="84" height="30" rx="3"
            fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
        <line x1="96" y1="43" x2="140" y2="43" stroke="rgba(255,255,255,0.25)" strokeWidth="2" strokeLinecap="round"/>
        <line x1="96" y1="50" x2="152" y2="50" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="96" y1="57" x2="128" y2="57" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" strokeLinecap="round"/>
        {/* Cards */}
        {[0,1].map(i => (
            <g key={i}>
                <rect x={88+i*44} y="72" width="38" height="40" rx="3"
                    fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"
                    style={{ animation: `ux-float ${2+i*0.6}s ease-in-out ${i*0.5}s infinite` }}/>
                <line x1={94+i*44} y1="82" x2={118+i*44} y2="82" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1={94+i*44} y1="88" x2={122+i*44} y2="88" stroke="rgba(255,255,255,0.12)" strokeWidth="1" strokeLinecap="round"/>
                <line x1={94+i*44} y1="94" x2={114+i*44} y2="94" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeLinecap="round"/>
            </g>
        ))}
        {/* CTA button */}
        <rect x="100" y="120" width="60" height="14" rx="7"
            fill="rgba(206,255,26,0.15)" stroke="rgba(206,255,26,0.5)" strokeWidth="1"
            style={{ animation: 'ux-glow-pulse 2s ease-in-out 0.5s infinite' }}/>
        <line x1="118" y1="127" x2="142" y2="127" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round"/>
        {/* Scanning line that sweeps down the phone */}
        <clipPath id="phone-clip">
            <rect x="81" y="6" width="98" height="148" rx="11"/>
        </clipPath>
        <rect x="81" y="6" width="98" height="3" rx="1"
            fill="rgba(206,255,26,0.25)"
            clipPath="url(#phone-clip)"
            style={{ animation: 'ux-scan 2.8s ease-in-out 0.3s infinite' }}/>
    </svg>
);

/* ─── Illustration 04 — Prototyping ─── */
const IllustrationPrototyping = () => (
    <svg viewBox="0 0 260 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        {/* Three screens */}
        {[20,100,180].map((x, i) => (
            <g key={i} style={{ animation: i === 1 ? 'ux-float 3s ease-in-out infinite' : `ux-float ${3.5 + i*0.4}s ease-in-out ${i*0.3}s infinite` }}>
                <rect x={x} y="20" width="60" height="90" rx="6"
                    fill="rgba(255,255,255,0.03)"
                    stroke={i===1 ? '#ceff1a' : 'rgba(255,255,255,0.12)'}
                    strokeWidth={i===1 ? '1.5' : '1'}
                    style={i===1 ? { animation: 'ux-node-pulse 2s ease-in-out infinite, ux-float 3s ease-in-out infinite' } : {}}/>
                <rect x={x+5} y="28" width="50" height="8" rx="2"
                    fill={i===1 ? 'rgba(206,255,26,0.15)' : 'rgba(255,255,255,0.06)'}/>
                <rect x={x+5} y="42" width="50" height="28" rx="2"
                    fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                <rect x={x+5} y="76" width="22" height="8" rx="4"
                    fill={i===1 ? 'rgba(206,255,26,0.2)' : 'rgba(255,255,255,0.08)'}/>
                <rect x={x+32} y="76" width="22" height="8" rx="4"
                    fill="rgba(255,255,255,0.06)"/>
                <rect x={x+5} y="90" width="50" height="12" rx="3"
                    fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            </g>
        ))}
        {/* Animated arrows */}
        {[0,1].map(i => (
            <g key={i} style={{ animation: `ux-arrow-slide 1.4s ease-in-out ${i*0.5}s infinite` }}>
                <path d={`M${80+i*80} 65 L${100+i*80} 65`}
                    stroke="rgba(206,255,26,0.6)" strokeWidth="1.8" strokeLinecap="round"/>
                <path d={`M${96+i*80} 59 L${102+i*80} 65 L${96+i*80} 71`}
                    stroke="rgba(206,255,26,0.6)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
        ))}
        {/* User cursor at bottom */}
        <g style={{ animation: 'ux-cursor-move 5s ease-in-out infinite' }}>
            <circle cx="130" cy="135" r="8" fill="rgba(206,255,26,0.1)" stroke="rgba(206,255,26,0.5)" strokeWidth="1"/>
            <circle cx="130" cy="132" r="3" fill="rgba(206,255,26,0.6)"/>
            <path d="M124 140 c0-3.3 2.7-6 6-6s6 2.7 6 6" fill="rgba(206,255,26,0.3)"/>
        </g>
    </svg>
);

/* ─── Illustration 05 — UI Design ─── */
const IllustrationUIDesign = () => (
    <svg viewBox="0 0 260 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        {/* Palette swatches with staggered pop */}
        {['#ceff1a','#3730a3','#ffffff','rgba(206,255,26,0.4)','rgba(55,48,163,0.5)'].map((c,i) => (
            <circle key={i} cx={40+i*28} cy="30" r="14" fill={c}
                stroke="rgba(255,255,255,0.1)" strokeWidth="1"
                style={{
                    animation: `ux-palette-pop ${1.8 + i*0.2}s ease-in-out ${i*0.15}s infinite`,
                    ...(i===0 ? { filter:'drop-shadow(0 0 8px rgba(206,255,26,0.7))' } : {})
                }}/>
        ))}
        {/* Typography */}
        <text x="20" y="70" fill="white" fontSize="22" fontWeight="700" fontFamily="sans-serif" opacity="0.9"
            style={{ animation: 'ux-float 3.5s ease-in-out infinite' }}>Aa</text>
        <text x="20" y="90" fill="rgba(255,255,255,0.5)" fontSize="10" fontFamily="sans-serif">Heading / Display</text>
        <line x1="20" y1="98" x2="240" y2="98" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
        {/* Shimmer sweep over palette row */}
        <clipPath id="ux-palette-clip">
            <rect x="20" y="14" width="170" height="32" rx="16"/>
        </clipPath>
        <rect x="20" y="14" width="30" height="32" rx="0"
            fill="rgba(255,255,255,0.08)"
            clipPath="url(#ux-palette-clip)"
            style={{ animation: 'ux-shimmer 2.5s linear 1s infinite' }}/>
        {/* Components */}
        <rect x="20" y="108" width="60" height="24" rx="12"
            fill="rgba(206,255,26,0.15)" stroke="#ceff1a" strokeWidth="1.2"
            style={{ animation: 'ux-glow-pulse 2.5s ease-in-out infinite' }}/>
        <line x1="38" y1="120" x2="62" y2="120" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round"/>
        <rect x="92" y="108" width="60" height="24" rx="12"
            fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.2)" strokeWidth="1"
            style={{ animation: 'ux-float 3s ease-in-out 0.4s infinite' }}/>
        <line x1="110" y1="120" x2="134" y2="120" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round"/>
        <rect x="164" y="108" width="24" height="24" rx="6"
            fill="rgba(55,48,163,0.3)" stroke="rgba(55,48,163,0.6)" strokeWidth="1"
            style={{ animation: 'ux-palette-pop 2s ease-in-out 0.8s infinite' }}/>
        <path d="M170 118 l4 4 8-6" stroke="rgba(206,255,26,0.8)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

/* ─── Illustration 06 — Usability Testing ─── */
const IllustrationTesting = () => (
    <svg viewBox="0 0 260 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        {/* Browser frame */}
        <rect x="60" y="10" width="140" height="100" rx="8"
            fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"/>
        <rect x="60" y="10" width="140" height="18" rx="8"
            fill="rgba(255,255,255,0.05)"/>
        <circle cx="73" cy="19" r="3" fill="rgba(255,100,100,0.6)"/>
        <circle cx="83" cy="19" r="3" fill="rgba(255,200,50,0.6)"/>
        <circle cx="93" cy="19" r="3" fill="rgba(100,200,100,0.6)"/>
        {/* Content lines */}
        <rect x="72" y="36" width="116" height="8" rx="2" fill="rgba(255,255,255,0.08)"/>
        <rect x="72" y="50" width="80" height="8" rx="2" fill="rgba(255,255,255,0.05)"/>
        <rect x="72" y="64" width="96" height="8" rx="2" fill="rgba(255,255,255,0.05)"/>
        <rect x="72" y="80" width="50" height="18" rx="9"
            fill="rgba(206,255,26,0.15)" stroke="rgba(206,255,26,0.5)" strokeWidth="1"
            style={{ animation: 'ux-glow-pulse 2.2s ease-in-out infinite' }}/>
        <line x1="82" y1="89" x2="112" y2="89" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round"/>
        {/* Moving cursor */}
        <g style={{ animation: 'ux-cursor-move 4s ease-in-out infinite' }}>
            <path d="M148 72 L142 90 L148 86 L152 94 L155 92 L151 84 L158 82 Z"
                fill="#ceff1a" opacity="0.85"
                style={{ animation: 'ux-wiggle 3s ease-in-out infinite', transformOrigin: '150px 83px',
                    filter: 'drop-shadow(0 0 8px rgba(206,255,26,0.7))' }}/>
        </g>
        {/* Annotation dot + line — animated draw */}
        <circle cx="72" cy="36" r="3" fill="rgba(206,255,26,0.7)"
            style={{ animation: 'ux-blink 2s ease-in-out infinite' }}/>
        <line x1="72" y1="36" x2="38" y2="22" stroke="rgba(206,255,26,0.4)" strokeWidth="1"
            strokeDasharray="40" strokeDashoffset="40"
            style={{ animation: 'ux-dash-draw 1s ease-out 0.5s forwards' }}/>
        <circle cx="36" cy="21" r="3" fill="rgba(206,255,26,0.5)"
            style={{ animation: 'ux-glow-pulse 2s ease-in-out 0.5s infinite' }}/>
        {/* Spec note box */}
        <line x1="122" y1="89" x2="215" y2="122" stroke="rgba(206,255,26,0.3)" strokeWidth="1"
            strokeDasharray="50" strokeDashoffset="50"
            style={{ animation: 'ux-dash-draw 1s ease-out 0.8s forwards' }}/>
        <rect x="205" y="120" width="50" height="22" rx="4"
            fill="rgba(206,255,26,0.08)" stroke="rgba(206,255,26,0.3)" strokeWidth="1"
            style={{ animation: 'ux-float 2.8s ease-in-out 0.8s infinite' }}/>
        <line x1="210" y1="128" x2="250" y2="128" stroke="rgba(206,255,26,0.4)" strokeWidth="1" strokeLinecap="round"/>
        <line x1="210" y1="134" x2="242" y2="134" stroke="rgba(206,255,26,0.25)" strokeWidth="1" strokeLinecap="round"/>
    </svg>
);

const steps = [
    {
        num: '01',
        title: 'Understand',
        desc: 'We analyse users, business goals, workflows, and behavioural patterns to build a clear picture of what success looks like for your product.',
        Illustration: IllustrationCompetitor,
    },
    {
        num: '02',
        title: 'Structure',
        desc: 'We create information architecture and user flows that reduce complexity — making it easy for users to find what they need and complete their goals.',
        Illustration: IllustrationSiteMap,
    },
    {
        num: '03',
        title: 'Design',
        desc: 'Interfaces are designed to feel intuitive, familiar, and efficient — crafted with precision using the right typography, colour systems, and interaction patterns.',
        Illustration: IllustrationWireframe,
    },
    {
        num: '04',
        title: 'Validate',
        desc: 'Through testing and real user feedback, we identify friction points and opportunities for improvement before your product reaches the market.',
        Illustration: IllustrationPrototyping,
    },
    {
        num: '05',
        title: 'Optimise',
        desc: 'UX evolves continuously. Through data, behavioural insights, and ongoing user feedback, we refine and improve experiences over time.',
        Illustration: IllustrationUIDesign,
    },
    {
        num: '06',
        title: 'The Lead Workz Approach',
        desc: 'Every step is grounded in empathy for the user and respect for business objectives — because the best UX is one that works for both.',
        Illustration: IllustrationTesting,
    },
];

export default function UXApproach() {
    const [hovered, setHovered] = useState<number | null>(null);

    return (
        <section className="relative overflow-hidden py-24 px-6 md:px-16 lg:px-24" style={{ background: '#0f0f0f' }}>
            <AnimStyles />

            {/* Subtle dot grid */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
                backgroundImage: 'radial-gradient(circle, rgba(206,255,26,1) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
            }} />
            <div className="absolute top-0 left-1/4 pointer-events-none" style={{
                width: 600, height: 600,
                background: 'radial-gradient(circle, rgba(206,255,26,0.04) 0%, transparent 70%)',
                transform: 'translateY(-30%)',
            }} />

            <div className="max-w-[1400px] mx-auto flex flex-col gap-16 relative z-10">

                {/* Heading */}
                <div className="flex flex-col items-center gap-4 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.25em] uppercase"
                        style={{ color: 'rgba(206,255,26,0.6)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                        How We Work
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-4xl md:text-6xl font-bold tracking-wide leading-tight"
                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff' }}>
                        Our UI/UX Design{' '}
                        <span style={{ color: '#ceff1a' }}>Process</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}
                        className="text-base md:text-lg max-w-2xl"
                        style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.02em' }}>
                        A structured, research-led approach that turns complexity into clarity — and clarity into experiences users love.
                    </motion.p>
                </div>

                {/* 2-column card grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {steps.map((step, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            onMouseEnter={() => setHovered(i)}
                            onMouseLeave={() => setHovered(null)}
                            className="relative rounded-2xl overflow-hidden cursor-default"
                            style={{
                                background: hovered === i ? 'rgba(206,255,26,0.04)' : 'rgba(255,255,255,0.03)',
                                border: hovered === i ? '1px solid rgba(206,255,26,0.25)' : '1px solid rgba(255,255,255,0.08)',
                                transition: 'background 0.35s, border-color 0.35s',
                                boxShadow: hovered === i ? '0 0 40px rgba(206,255,26,0.06) inset' : 'none',
                            }}
                        >
                            {/* Top: number, title, desc */}
                            <div className="p-7 pb-4 flex flex-col gap-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-black font-mono"
                                        style={{ color: '#ceff1a', letterSpacing: '0.08em',
                                            textShadow: hovered === i ? '0 0 12px rgba(206,255,26,0.7)' : 'none',
                                            transition: 'text-shadow 0.3s' }}>
                                        {step.num}
                                    </span>
                                    <div style={{ flex: 1, height: 1,
                                        background: hovered === i ? 'rgba(206,255,26,0.2)' : 'rgba(255,255,255,0.06)',
                                        transition: 'background 0.3s' }} />
                                </div>
                                <h3 className="text-xl md:text-2xl font-bold"
                                    style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                        color: hovered === i ? '#ceff1a' : 'rgba(255,255,255,0.85)',
                                        letterSpacing: '0.02em', transition: 'color 0.3s' }}>
                                    {step.title}
                                </h3>
                                <p className="text-sm md:text-base leading-relaxed"
                                    style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                        color: 'rgba(255,255,255,0.5)', letterSpacing: '0.02em' }}>
                                    {step.desc}
                                </p>
                            </div>

                            {/* Animated illustration */}
                            <div className="px-7 pb-7" style={{ height: 160 }}>
                                <step.Illustration />
                            </div>

                            {/* Watermark number */}
                            <div className="absolute top-5 right-6 select-none pointer-events-none"
                                style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                    fontSize: '64px', fontWeight: 900,
                                    color: 'rgba(255,255,255,0.03)', lineHeight: 1 }}>
                                {step.num}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                    className="flex justify-center">
                    <Link href="/contact"
                        className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                        style={{ background: '#ceff1a', color: '#080400',
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif', letterSpacing: '0.08em' }}>
                        Start Your UX Project →
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}
